// src/pages/BrokerSettings.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./css/settings.css";
import { API_BASE, request, getCsrfToken } from "../services/server";
import { useAuth } from "../services/AuthContext";
import Main from "./Main";
import {
  SettingsShell,
  SettingsSidebarCard,
  SettingsHeader,
  SettingsCard,
  SettingsField,
  SettingsModal,
  SettingsEmptyState,
} from "./components/settingsComponents";

const ROLE_OPTIONS = [
  { value: "admin",       label: "Administratör" },
  { value: "full_access", label: "Full åtkomst" },
  { value: "member",      label: "Medlem" },
];

// Surfaced both in the invite form and in the members list — kept here
// so the wording stays in one place. Same descriptions live in the
// BillingSetupModal step 2 info-popover for consistency.
const ROLE_DESCRIPTIONS = [
  {
    value: "admin",
    label: "Administratör",
    desc: "Full åtkomst inklusive betalning och fakturahantering. Lägger till betalkort, aktiverar workspaces och bjuder in användare.",
  },
  {
    value: "full_access",
    label: "Full åtkomst",
    desc: "Samma som administratör förutom betalning. Kan aktivera workspaces om ett betalkort redan finns, men kan inte själv lägga till kort.",
  },
  {
    value: "member",
    label: "Medlem",
    desc: "Endast läsläge på de workspace de bjuds in till. Kan inte aktivera workspaces eller hantera medlemmar.",
  },
];

function roleLabel(role) {
  const opt = ROLE_OPTIONS.find((r) => r.value === role);
  return opt ? opt.label : role;
}

const MEMBERS_URL = `${API_BASE}/users/companies/me/members/`;

async function parseResponse(res, fallback) {
  const contentType = res.headers.get("content-type") || "";
  const raw = await res.text();

  if (!res.ok) {
    if (contentType.includes("application/json")) {
      try {
        const json = JSON.parse(raw);
        throw new Error(json.detail || fallback);
      } catch {
        throw new Error(fallback);
      }
    }
    throw new Error(raw || fallback);
  }

  if (!raw) return null;
  if (!contentType.includes("application/json")) throw new Error("Endpoint did not return JSON.");
  return JSON.parse(raw);
}

export default function BrokerSettings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const currentEmail = user?.user?.email || user?.user?.username || user?.email || user?.username || "—";

  async function handleLogout() {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      navigate("/login");
    } catch {
      setLoggingOut(false);
    }
  }

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState("");

  const [selectedMember, setSelectedMember] = useState(null);
  const [editRole, setEditRole] = useState("member");
  const [editActive, setEditActive] = useState(true);
  const [savingEdit, setSavingEdit] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => { loadMembers(); }, []);

  async function loadMembers() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(MEMBERS_URL, { method: "GET", credentials: "include" });
      const data = await parseResponse(res, "Det gick inte att ladda medlemmar.");
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Det gick inte att ladda medlemmar.");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }

  async function inviteMember() {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setInviting(true);
    setError("");
    setInviteSuccess("");
    try {
      const res = await fetch(`${API_BASE}/users/invitations/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, role: inviteRole }),
      });
      await parseResponse(res, "Det gick inte att skicka inbjudan.");
      setInviteEmail("");
      setInviteRole("member");
      setInviteSuccess(`Inbjudan skickad till ${email}`);
    } catch (err) {
      setError(err.message || "Det gick inte att skicka inbjudan.");
    } finally {
      setInviting(false);
    }
  }

  function openEdit(member) {
    setSelectedMember(member);
    setEditRole(member.role || "member");
    setEditActive(Boolean(member.is_active));
  }

  function closeEdit() {
    setSelectedMember(null);
    setEditRole("member");
    setEditActive(true);
  }

  async function saveMember() {
    if (!selectedMember) return;
    setSavingEdit(true);
    setError("");
    try {
      const res = await fetch(`${MEMBERS_URL}${selectedMember.user_id}/`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: editRole, is_active: editActive }),
      });
      await parseResponse(res, "Det gick inte att uppdatera medlemmen.");
      await loadMembers();
      closeEdit();
    } catch (err) {
      setError(err.message || "Det gick inte att uppdatera medlemmen.");
    } finally {
      setSavingEdit(false);
    }
  }

  async function removeMember() {
    if (!selectedMember) return;
    setRemoving(true);
    setError("");
    try {
      const res = await fetch(`${MEMBERS_URL}${selectedMember.user_id}/`, {
        method: "DELETE",
        credentials: "include",
      });
      await parseResponse(res, "Det gick inte att inaktivera medlemmen.");
      await loadMembers();
      closeEdit();
    } catch (err) {
      setError(err.message || "Det gick inte att inaktivera medlemmen.");
    } finally {
      setRemoving(false);
    }
  }

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return (a.username || a.email || "").localeCompare(b.username || b.email || "");
    });
  }, [members]);

  const sidebar = (
    <SettingsSidebarCard title="Bjud in">
      <div className="st-invite-form">
        <input
          className="st-input"
          type="email"
          placeholder="E-postadress"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && inviteMember()}
        />
        <div className="st-invite-row">
          <select
            className="st-input"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="st-btn st-btn--primary"
            onClick={inviteMember}
            disabled={inviting || !inviteEmail.trim()}
          >
            {inviting ? "Skickar…" : "Skicka"}
          </button>
        </div>
        {inviteSuccess && <p className="st-success">{inviteSuccess}</p>}
      </div>
    </SettingsSidebarCard>
  );

  return (
    <Main>
      <SettingsShell sidebar={sidebar}>
        {error && <div className="st-error">{error}</div>}

        <SettingsHeader
          title="Företagsmedlemmar"
          subtitle="Hantera användare i ditt företag."
        />

        <SettingsCard>
          <div style={{
            padding: "12px 14px",
            background: "#F5F5F4",
            border: "1px solid #E7E5E4",
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 12,
            color: "#374151",
            lineHeight: 1.6,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: "#1C1917" }}>
              Roller
            </div>
            {ROLE_DESCRIPTIONS.map((r) => (
              <div key={r.value} style={{ marginBottom: 4 }}>
                <strong>{r.label}:</strong> {r.desc}
              </div>
            ))}
          </div>
        </SettingsCard>

        <SettingsCard>
          {loading ? (
            <SettingsEmptyState>Laddar…</SettingsEmptyState>
          ) : sortedMembers.length === 0 ? (
            <SettingsEmptyState>Inga användare ännu.</SettingsEmptyState>
          ) : (
            <div className="st-user-list">
              {sortedMembers.map((member) => (
                <button
                  key={member.user_id}
                  type="button"
                  className="st-user-card"
                  onClick={() => openEdit(member)}
                >
                  <div className="st-user-card-top">
                    <span className="st-user-name">
                      {member.username || member.email || `Användare ${member.user_id}`}
                    </span>
                    <span className={`st-badge${!member.is_active ? " st-badge--muted" : ""}`}>
                      {roleLabel(member.role)}
                    </span>
                  </div>
                  <div className="st-user-email">{member.email || "—"}</div>
                  <div className="st-user-meta">{member.is_active ? "Aktiv" : "Inaktiv"}</div>
                </button>
              ))}
            </div>
          )}
        </SettingsCard>

        {/* ── Signing Templates ── */}
        <SigningTemplatesSection />

        {/* ── Account ── */}
        <SettingsHeader title="Konto" subtitle="Ditt Qada-konto." />
        <SettingsCard>
          <div className="st-danger-row" style={{ marginBottom: 8 }}>
            <div>
              <div className="st-row-title">Inloggad som</div>
              <div className="st-row-desc">{currentEmail}</div>
            </div>
          </div>
          <div className="st-danger-row">
            <div>
              <div className="st-row-title">Logga ut</div>
              <div className="st-row-desc">Avsluta sessionen och gå tillbaka till inloggningssidan.</div>
            </div>
            <button
              className="st-btn st-btn--secondary"
              onClick={handleLogout}
              disabled={loggingOut}
            >
              {loggingOut ? "Loggar ut…" : "Logga ut"}
            </button>
          </div>
        </SettingsCard>
      </SettingsShell>

      <SettingsModal
        open={Boolean(selectedMember)}
        title="Redigera medlem"
        subtitle={selectedMember?.email || selectedMember?.username || ""}
        onClose={closeEdit}
        actions={
          <>
            <button
              type="button"
              className="st-btn st-btn--ghost-danger"
              onClick={removeMember}
              disabled={removing || savingEdit}
            >
              {removing ? "Inaktiverar…" : "Inaktivera"}
            </button>
            <button
              type="button"
              className="st-btn st-btn--secondary"
              onClick={closeEdit}
              disabled={removing || savingEdit}
            >
              Avbryt
            </button>
            <button
              type="button"
              className="st-btn st-btn--primary"
              onClick={saveMember}
              disabled={savingEdit || removing}
            >
              {savingEdit ? "Sparar…" : "Spara"}
            </button>
          </>
        }
      >
        <SettingsField label="Roll">
          <select
            className="st-input"
            value={editRole}
            onChange={(e) => setEditRole(e.target.value)}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </SettingsField>

        <label className="st-check-row">
          <input
            type="checkbox"
            checked={editActive}
            onChange={(e) => setEditActive(e.target.checked)}
          />
          <span>Aktiv medlem</span>
        </label>
      </SettingsModal>
    </Main>
  );
}


// ── Signing Templates Section ──────────────────────────────────────────────

const DOC_TYPES = [
  { value: "nda", label: "NDA / Sekretessavtal" },
  { value: "loi", label: "LOI / Avsiktsförklaring" },
  { value: "spa", label: "SPA / Aktieöverlåtelseavtal" },
  { value: "dd_agreement", label: "DD-avtal" },
  { value: "custom", label: "Övrigt" },
];

function SigningTemplatesSection() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Upload form
  const [uploadName, setUploadName] = useState("");
  const [uploadType, setUploadType] = useState("nda");
  const [uploadOrder, setUploadOrder] = useState(1);
  const [uploadRequired, setUploadRequired] = useState(true);
  const [uploadFile, setUploadFile] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await request("/signing/templates/");
      setTemplates(Array.isArray(data) ? data : []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  async function handleUpload() {
    if (!uploadFile || !uploadName.trim()) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", uploadFile);
      form.append("name", uploadName.trim());
      form.append("document_type", uploadType);
      form.append("send_order", uploadOrder);
      form.append("is_required", uploadRequired);

      const csrf = getCsrfToken();
      await fetch(`${API_BASE}/signing/templates/`, {
        method: "POST",
        credentials: "include",
        headers: csrf ? { "X-CSRFToken": csrf } : {},
        body: form,
      });

      setUploadName("");
      setUploadFile(null);
      setShowUpload(false);
      loadTemplates();
    } catch {}
    setUploading(false);
  }

  async function handleDelete(id) {
    try {
      await request(`/signing/templates/${id}/`, { method: "DELETE" });
      loadTemplates();
    } catch {}
  }

  return (
    <>
      <SettingsHeader
        title="Signeringsmallar"
        subtitle="Ladda upp dokumentmallar (NDA, LOI, SPA) som används vid signering. Ordningen styr vilka som skickas först."
        action={
          <button className="st-btn st-btn--primary" onClick={() => setShowUpload(!showUpload)}>
            + Lägg till mall
          </button>
        }
      />

      {showUpload && (
        <SettingsCard>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input
              className="st-input"
              placeholder="Mallnamn (t.ex. 'Standard NDA')"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <select className="st-input" value={uploadType} onChange={(e) => setUploadType(e.target.value)}>
                {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <input
                className="st-input"
                type="number"
                min="1"
                max="99"
                value={uploadOrder}
                onChange={(e) => setUploadOrder(parseInt(e.target.value) || 1)}
                style={{ width: 80 }}
                title="Skickaordning"
              />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#78716C" }}>
              <input
                type="checkbox"
                checked={uploadRequired}
                onChange={(e) => setUploadRequired(e.target.checked)}
                style={{ accentColor: "#57534E" }}
              />
              Obligatoriskt (måste signeras före nästa dokument)
            </label>
            <div
              onClick={() => document.getElementById("signing-template-file")?.click()}
              style={{
                border: "2px dashed #E7E5E4", borderRadius: 8, padding: 20,
                textAlign: "center", cursor: "pointer", background: uploadFile ? "#F5F5F4" : "#FAFAF9",
              }}
            >
              {uploadFile ? (
                <span style={{ fontSize: 13, color: "#1C1917", fontWeight: 600 }}>{uploadFile.name}</span>
              ) : (
                <span style={{ fontSize: 13, color: "#A8A29E" }}>Klicka för att välja PDF eller Word-dokument</span>
              )}
              <input
                id="signing-template-file"
                type="file"
                accept=".pdf,.docx,.doc"
                style={{ display: "none" }}
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="st-btn st-btn--primary" onClick={handleUpload} disabled={uploading || !uploadFile || !uploadName.trim()}>
                {uploading ? "Laddar upp…" : "Ladda upp"}
              </button>
              <button className="st-btn" onClick={() => { setShowUpload(false); setUploadFile(null); }}>Avbryt</button>
            </div>
          </div>
        </SettingsCard>
      )}

      <SettingsCard>
        {loading ? (
          <SettingsEmptyState>Laddar…</SettingsEmptyState>
        ) : templates.length === 0 ? (
          <SettingsEmptyState>Inga signeringsmallar. Klicka "Lägg till mall" ovan.</SettingsEmptyState>
        ) : (
          <div className="st-member-list">
            {templates.map((t) => (
              <div key={t.id} className="st-member-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div className="st-user-name">{t.name}</div>
                  <div className="st-user-meta">
                    {t.document_type_label} &middot; Ordning: {t.send_order}
                    {t.is_required && <span style={{ color: "#DC2626", marginLeft: 8 }}>Obligatorisk</span>}
                  </div>
                  <div className="st-user-meta">{t.original_filename}</div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {t.builder_url && (
                    <a
                      href={t.builder_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="st-btn"
                      style={{ fontSize: 12, color: "#57534E", textDecoration: "none" }}
                    >
                      Placera signaturer
                    </a>
                  )}
                  <button
                    className="st-btn"
                    style={{ color: "#DC2626", fontSize: 12 }}
                    onClick={() => handleDelete(t.id)}
                  >
                    Ta bort
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SettingsCard>
    </>
  );
}
